import { useState } from 'react';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { FormField, Textarea } from '../../components/ui/Field';
import { useSettingsSave, SaveRow } from './settingsSave';

/**
 * Personal Voice — the per-person instructions injected into every AI/LLM request.
 *
 * Lives on **My Profile**, not App Settings: it is a property of the person, not of the workspace
 * or the install. App Settings is where a workspace admin configures things that affect everyone;
 * this affects only its owner, and sitting there invited the reading that it was a shared setting.
 * Company Voice remains a workspace setting and stays where it is.
 *
 * Mount it keyed on the current value (`key={`voice-${user?.personalVoice ?? ''}`}`) so the
 * uncontrolled initial state is re-seeded when the principal refreshes after a save.
 */

const PERSONAL_HINT =
  'Describe your role, personality, and the creative outcomes you want from the AI/LLM tools. This guides responses generated for you.';

export function VoiceCard({
  initialPersonal,
  onSaved,
}: {
  initialPersonal: string;
  onSaved: () => Promise<unknown> | void;
}) {
  const [personalVoice, setPersonalVoice] = useState(initialPersonal);
  const { saving, saved, setSaved, error, save } = useSettingsSave(onSaved);

  const dirty = personalVoice !== initialPersonal;

  return (
    <Card>
      <CardHeader
        title="Voice & guidance"
        description="These instructions are injected into the system prompt for every AI/LLM request."
      />
      <CardBody className="space-y-5">
        <FormField label="Personal Voice" htmlFor="personal-voice" hint={PERSONAL_HINT}>
          <Textarea
            id="personal-voice"
            rows={6}
            className="min-h-[9rem]"
            value={personalVoice}
            onChange={(e) => {
              setSaved(false);
              setPersonalVoice(e.target.value);
            }}
            placeholder="e.g. I'm a B2B marketing lead. Keep copy confident, concise, and jargon-free…"
          />
        </FormField>

        <SaveRow
          saving={saving}
          saved={saved}
          dirty={dirty}
          error={error}
          onSave={() => void save({ personalVoice })}
        />
      </CardBody>
    </Card>
  );
}
